import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Download, Loader2, FileSpreadsheet } from 'lucide-react';
import * as XLSX from 'xlsx';

interface Application {
  application_id: string;
  application_number: string;
  application_data?: any;
}

interface ExportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  applications: Application[];
  onExport?: (selectedIds: string[]) => void;
}

export function ExportModal({ open, onOpenChange, applications, onExport }: ExportModalProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isExporting, setIsExporting] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');

  // Filter applications based on search
  const filteredApplications = applications.filter((app) => {
    const searchLower = searchFilter.toLowerCase();
    const appNumber = app.application_number?.toLowerCase() || '';
    const appData = app.application_data;
    const title = appData?.applicationMetaData?.inventionTitle?.toLowerCase() || '';
    const inventor = appData?.applicationMetaData?.firstInventorName?.toLowerCase() || '';
    const docketNumber = appData?.applicationMetaData?.docketNumber?.toLowerCase() || '';

    return (
      appNumber.includes(searchLower) ||
      title.includes(searchLower) ||
      inventor.includes(searchLower) ||
      docketNumber.includes(searchLower)
    );
  });

  const toggleSelection = (appId: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(appId)) {
      newSelected.delete(appId);
    } else {
      newSelected.add(appId);
    }
    setSelectedIds(newSelected);
  };

  const selectAll = () => {
    setSelectedIds(new Set(filteredApplications.map((app) => app.application_id)));
  };

  const deselectAll = () => {
    setSelectedIds(new Set());
  };

  const handleExport = async () => {
    setIsExporting(true);

    try {
      // Get selected applications
      const selectedApps = applications.filter((app) => selectedIds.has(app.application_id));

      // Prepare data for Excel
      const excelData = selectedApps.map((app) => {
        const appData = app.application_data;
        const metaData = appData?.applicationMetaData || {};

        return {
          'Application Number': app.application_number || '',
          'Title': metaData.inventionTitle || '',
          'Filing Date': metaData.filingDate || '',
          'Status': metaData.applicationStatusDescriptionText || '',
          'Patent Number': metaData.patentNumber || '',
          'Grant Date': metaData.grantDate || '',
          'Publication Number': metaData.earliestPublicationNumber || '',
          'Publication Date': metaData.earliestPublicationDate || '',
          'First Inventor': metaData.firstInventorName || '',
          'Examiner': metaData.examinerNameText || '',
          'Art Unit': metaData.groupArtUnitNumber || '',
          'Docket Number': metaData.docketNumber || '',
          'Confirmation Number': metaData.applicationConfirmationNumber || '',
        };
      });

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      const columnWidths = [
        { wch: 15 }, // Application Number
        { wch: 50 }, // Title
        { wch: 12 }, // Filing Date
        { wch: 30 }, // Status
        { wch: 12 }, // Patent Number
        { wch: 12 }, // Grant Date
        { wch: 15 }, // Publication Number
        { wch: 12 }, // Publication Date
        { wch: 25 }, // First Inventor
        { wch: 25 }, // Examiner
        { wch: 10 }, // Art Unit
        { wch: 15 }, // Docket Number
        { wch: 15 }, // Confirmation Number
      ];
      worksheet['!cols'] = columnWidths;

      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Applications');

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `USPTO_Applications_Export_${timestamp}.xlsx`;

      // Download file
      XLSX.writeFile(workbook, filename);

      // Call onExport callback if provided
      if (onExport) {
        onExport(Array.from(selectedIds));
      }

      // Close modal
      onOpenChange(false);
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Failed to export data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Export Applications to Excel
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-4">
          {/* Search and Select All */}
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Search by application number, title, inventor, or docket number..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {selectedIds.size} of {filteredApplications.length} selected
                {searchFilter && ` (filtered from ${applications.length} total)`}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={selectAll}
                  disabled={filteredApplications.length === 0}
                >
                  Select All
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={deselectAll}
                  disabled={selectedIds.size === 0}
                >
                  Deselect All
                </Button>
              </div>
            </div>
          </div>

          {/* Application List */}
          <div className="flex-1 overflow-y-auto border border-gray-200 rounded-md">
            {filteredApplications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                {searchFilter ? 'No applications match your search' : 'No applications available'}
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredApplications.map((app) => {
                  const appData = app.application_data;
                  const metaData = appData?.applicationMetaData || {};
                  const isSelected = selectedIds.has(app.application_id);

                  return (
                    <div
                      key={app.application_id}
                      className={`p-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                        isSelected ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => toggleSelection(app.application_id)}
                    >
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleSelection(app.application_id)}
                          className="mt-1"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2 mb-1">
                            <span className="font-medium text-sm text-gray-900">
                              {app.application_number}
                            </span>
                            {metaData.patentNumber && (
                              <span className="text-xs text-green-600">
                                Granted: {metaData.patentNumber}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-700 line-clamp-1">
                            {metaData.inventionTitle || 'No title available'}
                          </p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                            {metaData.firstInventorName && (
                              <span>{metaData.firstInventorName}</span>
                            )}
                            {metaData.filingDate && (
                              <span>Filed: {new Date(metaData.filingDate).toLocaleDateString()}</span>
                            )}
                            {metaData.applicationStatusDescriptionText && (
                              <span className="truncate max-w-[200px]">
                                {metaData.applicationStatusDescriptionText}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            disabled={selectedIds.size === 0 || isExporting}
            className="gap-2"
          >
            {isExporting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Export {selectedIds.size} Application{selectedIds.size !== 1 ? 's' : ''}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
